/** @jsxImportSource @emotion/react */
import ScheduleIcon from "@mui/icons-material/Schedule";
import PhoneIcon from "@mui/icons-material/Phone";
import RoomIcon from "@mui/icons-material/Room";
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import React, {ReactNode, SyntheticEvent, useEffect, useMemo, useRef, useState} from "react";
import {
  addDays,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  endOfMonth,
  isSameDay,
  isSameMonth,
  previousSunday,
  startOfDay
} from "date-fns";
import {ButtonBase, IconButton, InputAdornment, Tab, Tabs, TextField} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {selectProductsByFilter, selectProductStatus} from "../reducks/products/selectors";
import {fetchProducts} from "../reducks/products/operations";
import {selectAppInfo} from "../reducks/info/selectors";
import {fetchAppInfo} from "../reducks/info/thunks";
import {selectCategories, selectCategoryStatus} from "../reducks/categories/selectors";
import {Category} from "../reducks/categories/types";
import {fetchCategories} from "../reducks/categories/thunks";
import {ProductCard} from "../components/Products/ProductCard";
import {RootState} from "../reducks/store/store";
import {setReceiveDate} from "../reducks/global/slice";
import {selectReceiveDate} from "../reducks/global/selectors";
import styled from "@emotion/styled";
import {Heading} from "../components/Uikit/Heading";
import {useSearchParams} from "react-router-dom";
import {resetCartAction} from "../reducks/users/actions";

/**
 * パンダッコの情報を表示する.
 */
const ShopInformation = () => {
  return <section className={"flex flex-col gap-4"}>
    <div className={"flex flex-col gap-1"}>
      <div className={'text-lg'}>パンダッコ</div>
      <div className={'text-base'}>天然素材.無添加パンで作るベーグルコッペパン</div>
    </div>
    <div className={"flex flex-col gap-2"}>
      <div className={"flex flex-row gap-2"}>
        <ScheduleIcon/>
        木金土日 10:00～16:00（売切れ次第終了）
      </div>
      <div className={"flex flex-row gap-2"}>
        <PhoneIcon/>
        06-6842-2780
      </div>
      <div className={"flex flex-row gap-2"}>
        <RoomIcon/>
        <a
          href='https://www.google.com/maps/place/%E3%83%91%E3%83%B3%E3%83%80%E3%83%83%E3%82%B3/@34.7848138,135.4705761,17z/data=!4m15!1m8!3m7!1s0x6000fac8d40dec4b:0xbd5d5a03581e8ca1!2z44CSNTYwLTAwMjIg5aSn6Ziq5bqc6LGK5Lit5biC5YyX5qGc5aGa77yT5LiB55uu77yY4oiS77yR77yXIOmrmOWztuODk-ODqw!3b1!8m2!3d34.7848138!4d135.473151!16s%2Fg%2F11fx54zyr9!3m5!1s0x6000fac8d46bfb3b:0xeffdb13e4fccb5ba!8m2!3d34.7848265!4d135.4731106!16s%2Fg%2F11gmwpsdby?entry=ttu'
          target='_blank'
          // noreferrer noopener がないとセキュリティ的にまずいらしい
          rel="noreferrer noopener">
          〒560-0022大阪府豊中市北桜塚3丁目8−17
        </a>
      </div>
    </div>
  </section>
}

/**
 * 日付配列を重複無しの月配列に変換する
 * @param dates 入力の日付
 */
function monthSet(dates: Date[]) {
  return Array.from(
    new Set(dates.map(d => `${d.getFullYear()}/${d.getMonth()}`))
  ).map(dateStr => {
    const splitted = dateStr.split('/');
    const date = new Date(
      Number(splitted[0]),  // year
      Number(splitted[1]),  // month (0-indexed)
      1                     // day
    );
    return startOfDay(date);
  });
}

/**
 * 引数の月のカレンダーに表示される日付のリストを返す
 * @param thisMonth カレンダーとして表示したい月の最初の日
 */
function getCalenderDates(thisMonth: Date) {
  const weeks: Date[][] = [];
  let date: Date = previousSunday(thisMonth);

  // Calculate the actual number of weeks needed from calendar start to month end
  const endDate = endOfMonth(thisMonth);
  const daysInRange = differenceInCalendarDays(endDate, date) + 1;
  const weeksNeeded = Math.ceil(daysInRange / 7);

  for (let week = 0; week < weeksNeeded; week++) {
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      dates.push(new Date(date));
      date = addDays(date, 1);
    }
    weeks.push(dates);
  }

  return weeks;
}

/**
 * カレンダー内の曜日
 */
const DayItem = ({color, children}: {
  color?: string,
  children: ReactNode
}) => {
  return <div className={`w-full text-center py-2 ${color ?? ""}`}>{children}</div>
}

/**
 * カレンダー内の日付
 */
const DateItem = ({children, disabled, selected, onClick}: {
  children: ReactNode,
  onClick: () => void,
  disabled: boolean,
  selected: boolean
}) => {
  return <ButtonBase disabled={disabled}
                     onClick={onClick}
                     sx={{
                       width: '100%', textAlign: "center", paddingY: "1rem",
                       color: disabled ? '#C5C5C5' : '', borderRadius: '10px',
                       backgroundColor: selected ? '#FFDAD6' : '',
                       border: selected ? '1px solid #9C413C' : ''
                     }}>
    {children}
  </ButtonBase>
}

const Calender = ({selected, includeDates, onClick}: {
  selected: Date | undefined,
  onClick: (date: Date) => void;
  includeDates: Date[]
}) => {
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
  const includeMonths = monthSet(includeDates);
  const currentMonth = useMemo(() =>
      includeMonths.length > 0 ? includeMonths[currentMonthIndex] : new Date(),
    [includeMonths, currentMonthIndex]);

  const isNewestMonth = includeMonths.find(d => d.getTime() > currentMonth.getTime()) === undefined;
  const isOldestMonth = includeMonths.find(d => d.getTime() < currentMonth.getTime()) === undefined;

  const weeksToDisplay = useMemo(() => getCalenderDates(currentMonth), [currentMonth]);

  const handleClickDate = (date: Date) => {
    if (!isSameMonth(date, currentMonth)) {
      setCurrentMonthIndex(prev => prev + differenceInCalendarMonths(date, currentMonth))
    }
    onClick(date);
  }

  return <div className={"flex flex-col gap-2"}>
    <div className={"flex flex-row justify-between items-center w-full"}>
      <IconButton disabled={isOldestMonth} color={"primary"}
                  onClick={() => setCurrentMonthIndex(prev => prev - 1)}>
        <ArrowBackIosNewRoundedIcon/>
      </IconButton>
      <div className={"text-xl"}>
        {currentMonth.getFullYear()}年 {currentMonth.getMonth() + 1}月
      </div>
      <IconButton disabled={isNewestMonth} color={"primary"}
                  onClick={() => setCurrentMonthIndex(prev => prev + 1)}>
        <ArrowForwardIosRoundedIcon/>
      </IconButton>
    </div>
    <div className={"flex flex-col"}>
      <div className={"flex flex-row w-full"}>
        <DayItem color={"text-calender-sunday"}>日</DayItem>
        <DayItem>月</DayItem>
        <DayItem>火</DayItem>
        <DayItem>水</DayItem>
        <DayItem>木</DayItem>
        <DayItem>金</DayItem>
        <DayItem color={"text-calender-saturday"}>土</DayItem>
      </div>
      <hr/>
      {weeksToDisplay.map(dates =>
        <div className={"flex flex-row w-full"} key={`row-${dates[0].toDateString()}`}>
          {dates.map(d =>
            <DateItem key={d.toDateString()}
                      selected={selected !== undefined && isSameDay(selected, d)}
                      disabled={includeDates.find(includeDate => isSameDay(includeDate, d)) === undefined}
                      onClick={() => handleClickDate(d)}>
              {d.getDate()}
            </DateItem>
          )}
        </div>
      )}
    </div>
  </div>
}

const CategoryTabs = ({categories, value, onChange}: {
  categories: Category[],
  onChange: (id: string) => void,
  value: string
}) => {
  const handleChange = (event: SyntheticEvent, value: string) => {
    onChange(value);
  }
  return <Tabs value={value} onChange={handleChange} color={'primary'} variant={'scrollable'}>
    {categories.map(c => <Tab label={c.name} value={c.id} key={c.id}/>)}
  </Tabs>
}

/**
 * コンポーネントのbottomにあるz-indexがFadeBackgroundより小さい要素をぼかす
 */
const FadeBackground = styled.div`
  z-index: 10;
  background: linear-gradient(rgba(255, 255, 255, 1) 90%, rgba(255, 255, 255, 0) 95%);
`;

const SearchField = ({value, onChange}: {
  value: string,
  onChange: (value: string) => void
}) => {
  const [val, setVal] = useState(value);

  const handleEnter = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') onChange(val);
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') onChange(value);
    setVal(value);
  }

  return <TextField label={"検索"} id={"search-products"} helperText={"確定して検索"}
                    inputMode={"search"}
                    value={val}
                    onKeyPress={handleEnter}
                    onChange={handleChange}
                    variant={"outlined"} color={"primary"}
                    InputProps={{
                      endAdornment:
                        <InputAdornment position={"end"}>
                          <IconButton onClick={() => onChange(val)}>
                            <SearchRoundedIcon/>
                          </IconButton>
                        </InputAdornment>
                    }}/>
}

const categoryOfAll: Category = {id: '', name: '全て', order: '000'};

const paramKeys = {
  keyword: 'q',
  category: 'category'
}

export default function HomePage() {
  const [category, setCategory] = useState(categoryOfAll.id);
  const [keyword, setKeyword] = useState('');
  const refOfProductSelectHeading = useRef<HTMLDivElement>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const _category = searchParams.get(paramKeys.category) ?? '';
    const _keyword = searchParams.get(paramKeys.keyword) ?? '';

    setCategory(_category);
    setKeyword(_keyword);
  }, [searchParams]);

  const dispatch = useDispatch();

  const onSelectDate = (date: Date) => {
    dispatch(setReceiveDate(date));
    dispatch(resetCartAction());
  }

  const handleKeyword = (val: string) => {
    setKeyword(val);
    setSearchParams(prev => {
      if (val === '') {
        prev.delete(paramKeys.keyword);
      } else {
        prev.set(paramKeys.keyword, val);
      }

      return prev;
    }, {preventScrollReset: true});
  }

  const handleCategory = (val: string) => {
    setCategory(val);
    setSearchParams(prev => {
      prev.set(paramKeys.category, val);
      return prev;
    }, {preventScrollReset: true});
  }

  const receiveDate = useSelector(selectReceiveDate);

  // 日付選択された後にスクロールする
  useEffect(() => {
    refOfProductSelectHeading.current?.scrollIntoView({block: "start", behavior: 'smooth'});
  }, [receiveDate])

  // region Fetching
  const appInfo = useSelector(selectAppInfo);
  useEffect(() => {
    if (appInfo.status === 'idle') dispatch(fetchAppInfo())
  }, [dispatch, appInfo.status]);

  const products = useSelector((state: RootState) =>
    selectProductsByFilter(state, category, keyword, 'time', receiveDate))
  const productStatus = useSelector(selectProductStatus);
  useEffect(() => {
    // appInfo が読み込まれて日付を選択している間、裏でfetchする
    if (productStatus === 'idle' && appInfo.status === 'succeeded') dispatch(fetchProducts());
  }, [dispatch, productStatus, appInfo]);

  const categories = [categoryOfAll, ...useSelector(selectCategories)];
  const categoryStatus = useSelector(selectCategoryStatus);
  useEffect(() => {
    // appInfo が読み込まれて日付を選択している間、裏でfetchする
    if (categoryStatus === 'idle' && appInfo.status === 'succeeded') dispatch(fetchCategories());
  }, [dispatch, categoryStatus, appInfo])
  // endregion

  const receivableDates = appInfo.data?.opening_dates.map(d => d.toDate()) ?? [];

  return (
    <div className={"px-4 flex flex-col gap-3 mt-4"}>
      <ShopInformation/>
      <Heading text={"受け取り日を選択してください"}/>
      <Calender selected={receiveDate} onClick={onSelectDate}
                includeDates={receivableDates}/>
      <div className={`transition-opacity ${receiveDate !== undefined ? '' : 'opacity-0'}`}>
        {
          receiveDate !== undefined &&
            <>
                <FadeBackground className={'flex flex-col gap-2 sticky top-0 -mb-6 pb-9 -mx-2 px-2 '}>
                    <Heading text={"商品を選択してください"} ref={refOfProductSelectHeading}/>
                    <div className={'flex flex-row gap-2 pt-4 pb-2'}>
                        <SearchField value={keyword} onChange={handleKeyword}/>
                    </div>
                    <div className={'-mx-4'}>
                        <CategoryTabs categories={categories} value={category} onChange={handleCategory}/>
                    </div>
                </FadeBackground>
                <div className={"flex flex-row flex-wrap -mx-2"}>
                  {
                    products.length === 0 ?
                      <div className={'w-full p-5 rounded-l border border-material-outline'}>
                        注文可能な商品がありません
                      </div>
                      :
                      products.map((product) => (
                        <ProductCard product={product} receiveDate={receiveDate} key={product.id}/>
                      ))
                  }
                </div>
            </>
        }
      </div>
    </div>
  )
}
